import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { axisBottom, axisLeft } from 'd3-axis';
import { format } from 'd3-format';
import { NumberValue, scaleBand, scaleLinear } from 'd3-scale';
import { pointer, select } from 'd3-selection';
import { AnchoredPosition } from '../callout/callout-position';
import { HookDirective } from '../core/hook';
import { useEffect, useState } from '../core/hooks';

//todo: unique ids

export type GroupData = {
  // name of group should be unique becuase of scaleBand
  id: string;
  name: string;
  members: GroupMember[];
}[];

type GroupMember = {
  name: string;
  min: number;
  avg: number;
  max: number;
  color?: string;
};

type Settings = {
  yLock?: number;
  yAdd?: number;
};

@Component({
  selector: 'floating-bar',
  templateUrl: './floating-bar.component.html',
  styleUrls: ['./floating-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FloatingBarComponent extends HookDirective {
  constructor(
    _cdr: ChangeDetectorRef,
    private cdr: ChangeDetectorRef,
  ) {
    super(_cdr);
  }

  @Input()
  settings: Settings = {};

  @Input()
  groupData: GroupData = [];

  @Input()
  title: string = '';

  @Input()
  emptyStateMessage: string = 'No data to display.';

  @Output()
  barClick = new EventEmitter<{ groupId: number; name: string }>();

  isSingleRate(min: number, max: number, average: number) {
    const epsilon = 0.001;
    if (!min && !max && !average) {
      return false;
    }
    return (
      Math.abs(min - average) < epsilon && Math.abs(max - average) < epsilon
    );
  }

  get _() {
    const margin = { top: 36, right: 32, bottom: 32, left: 60 };
    const headerHeight = 16;
    const [graphWidth, setGraphWidth] = useState(this.groupData.length * 200);
    const [containerWidth, setContainerWidth] = useState(0);
    const [height, setHeight] = useState(0);

    useEffect(() => {
      const groupWidth = Math.max(
        this.groupData.length * 200 - margin.left - margin.right,
        0,
      );
      if (groupWidth < containerWidth) {
        setGraphWidth(containerWidth);
      } else {
        setGraphWidth(groupWidth);
      }
    }, [this.groupData]);

    const x = scaleBand().range([0, graphWidth]).padding(0.2);

    const header = select('#header');
    header
      .selectAll('text.header')
      .data(['title'])
      .enter()
      .append('text')
      .attr('class', 'header')
      .attr('x', 0)
      .attr('y', 0)
      .text(this.title)
      .style('alignment-baseline', 'middle')
      .attr('font-weight', 400)
      .attr('font-size', 20)
      .attr('text-anchor', 'left');

    let runningWidth = 0;
    header
      .selectAll('text.legend')
      .data(this.groupData[0]?.members?.slice().reverse() ?? [])
      .join('text')
      .attr('class', 'legend')
      .text((d: any) => d.name)
      .style('alignment-baseline', 'middle')
      .attr('font-weight', 400)
      .attr('font-size', 12)
      .attr('y', 0)
      .attr('text-anchor', 'left')
      .attr('x', (d: any, i: number) => {
        const labelWidth = d.legendLabelWidth;
        const returnWidth = runningWidth + labelWidth + 10;
        runningWidth = returnWidth + 25; // for rect
        return containerWidth - returnWidth;
      });

    runningWidth = 0;
    header
      .selectAll('rect.legend')
      .data(this.groupData[0]?.members?.slice().reverse() ?? [])
      .join('rect')
      .attr('class', 'legend')
      .attr('width', 20)
      .attr('height', 20)
      .style('fill', (d: any) => d.color || '#51C2F2')
      .attr('y', -10)
      .attr('x', (d: any, i: number) => {
        const labelWidth = d.legendLabelWidth;
        const returnWidth = runningWidth + labelWidth + 10 + 25;
        runningWidth = returnWidth; // for rect
        return containerWidth - returnWidth;
      });

    const xAxisElem = select<SVGGElement, unknown>('#xaxis');
    const xAxis = axisBottom(x).tickSizeOuter(0);
    const y = scaleLinear().range([height - 30, 0]);
    const rotatedXLabels = new Set();
    useEffect(() => {
      xAxisElem
        .call(xAxis)
        .attr('transform', `translate(0,${height - 30})`)
        .attr('overflow', 'scroll')
        .attr('text-wrap', 'wrap')
        .call((g: any) => g.selectAll('.tick line').attr('stroke', '#9B96A4'))
        .call((g: any) =>
          g
            .selectAll('.tick text')
            .attr('font-size', '14')
            .attr('font-weight', '400')
            .attr('width', '100')
            .attr('text-wrap-mode', 'wrap')
            .attr('word-wrap', 'break-work')
            .each((d: any, i: any, n: NodeList) => {
              Array.from(Object.values(n)).forEach((labelElement: any) => {
                if (
                  labelElement.getComputedTextLength() >
                  graphWidth / (this.groupData?.length ?? 1)
                ) {
                  rotatedXLabels.add(labelElement.innerHTML);
                  labelElement.innerHTML =
                    labelElement.innerHTML.substring(0, 20) + '...';
                }
              });
            })
            .on('mouseover', (event: any, d: string) =>
              rotatedXLabels.has(d) ? setLabelHoverName(d) : null,
            )
            .on('mousemove', (event: any, d: string) =>
              rotatedXLabels.has(d) ? setLabelHoverName(d) : null,
            )
            .on('mouseleave', () => {
              setLabelHoverName('');
            }),
        )
        .call((g: any) => g.select('.domain').attr('stroke', '#9B96A4'));
    }, [height, graphWidth, this.groupData?.length]);

    const yAxisElem = select<SVGGElement, unknown>('#yaxis');
    const maxVal = Math.max(
      ...this.groupData.flatMap(e => e.members).flatMap(e => e.max),
    );
    const minVal = Math.min(
      ...this.groupData.flatMap(e => e.members).flatMap(e => e.min),
    );
    const maxY =
      Math.max(...this.groupData.flatMap(e => e.members).flatMap(e => e.max)) +
      (maxVal - minVal) / 10;
    const minY = Math.max(
      Math.min(...this.groupData.flatMap(e => e.members).flatMap(e => e.min)) -
        (maxVal - minVal) / 10,
      0,
    );

    const gridElem = select<SVGGElement, unknown>('#grid');

    const svgSize = (size: { width: number; height: number }) => {
      const elementWidth = size.width - margin.left - margin.right;
      const groupWidth = Math.max(
        this.groupData.length * 200 - margin.left - margin.right,
        0,
      );
      if (groupWidth < elementWidth) {
        setGraphWidth(elementWidth);
      } else {
        setGraphWidth(groupWidth);
      }
      setContainerWidth(elementWidth);
      setHeight(size.height - margin.top - margin.bottom);
    };

    select('#container').attr(
      'transform',
      `translate(${margin.left},${margin.top})`,
    );

    select('#chartcontainer').attr('transform', `translate(0,${headerHeight})`);

    const [currentHoverable, setCurrentHoverable] = useState({
      y: 0,
      x: 0,
      xLabel: '',
      hoverableX: 0,
      data: null,
    });

    const [calloutAnchor, setCalloutAnchor] = useState(new DOMRect(0, 0, 0, 0));

    const [toolTipVisible, setToolTipVisible] = useState(false);

    const setYHoverable = (event: MouseEvent) => {
      setCalloutAnchor(
        new DOMRect(event.clientX - 12, event.clientY - 12, 24, 24),
      );
      if (!toolTipVisible) return;
      currentHoverable.y = event.clientY;
    };

    const onLeave = () => {
      setToolTipVisible(false);
      setOnHoverGroup('');
    };

    const onHover = (value: any) => {
      setCurrentHoverable({ ...value });
      if (value.data) {
        setToolTipVisible(true);
      } else {
        setToolTipVisible(false);
      }
    };

    const calculateToolTipContent = (
      d: any,
      yValue: { interpretedValue: number; plusMinus: number },
    ) => {
      if (d.min == null && d.max == null && d.avg == null)
        return { data: null };
      if (this.isSingleRate(d.min, d.max, d.avg)) {
        return {
          data: [{ value: d.avg, label: 'Single rate', important: true }],
        };
      }
      const { interpretedValue, plusMinus } = yValue;

      if (
        +d.avg + plusMinus > interpretedValue &&
        interpretedValue > +d.avg - plusMinus
      ) {
        return {
          data: [
            { value: d.max, label: 'Max' },
            { value: d.avg, label: 'Avg', important: true },
            { value: d.min, label: 'Min' },
          ],
        };
      }

      if (interpretedValue < d.avg) {
        return {
          data: [
            { value: d.max, label: 'Max' },
            { value: d.avg, label: 'Avg' },
            { value: d.min, label: 'Min', important: true },
          ],
        };
      } else if (interpretedValue > d.avg) {
        return {
          data: [
            { value: d.max, label: 'Max', important: true },
            { value: d.avg, label: 'Avg' },
            { value: d.min, label: 'Min' },
          ],
        };
      } else {
        return {
          data: [
            { value: d.max, label: 'Max' },
            { value: d.avg, label: 'Avg', important: true },
            { value: d.min, label: 'Min' },
          ],
        };
      }
    };

    const interpretValue = (event: any, d: any) => {
      const domainMax = Math.max(...y.domain()); // values
      const domainMin = Math.min(...y.domain()); // values
      const rangeMax = Math.max(...y.range()); // pixels
      const interpretedValue =
        (1 - pointer(event)[1] / rangeMax) * (domainMax - domainMin) +
        domainMin;
      // 5 pixels of slush, on the domain scale:
      const plusMinus = (5 * (domainMax - domainMin)) / rangeMax;
      return { interpretedValue, plusMinus };
    };
    const [onHoverGroup, setOnHoverGroup] = useState('');

    const mouseOver = (event: any, d: any) => {
      const content = calculateToolTipContent(d, interpretValue(event, d));
      setOnHoverGroup(d.groupName + d.name);
      onHover({ ...content });
    };

    const [labelHoverName, setLabelHoverName] = useState('');

    const onClick = (event: any, d: any) => {
      this.barClick.emit({ groupId: d.groupId, name: d.name });
    };

    x.domain(this.groupData.map(group => group.name));
    y.domain([minY, maxY]);
    x.range([0, graphWidth]);

    const yAxis = axisLeft(y).tickSizeOuter(0).tickFormat(format('$'));
    yAxisElem
      .call(yAxis)
      .call((g: any) => g.selectAll('.tick line').attr('stroke', '#9B96A4'))
      .call((g: any) =>
        g
          .selectAll('.tick text')
          .attr('font-size', '14')
          .attr('font-weight', '400'),
      )
      .call((g: any) => g.select('.domain').attr('stroke', '#9B96A4'));

    const yGrid = axisLeft(y)
      .tickFormat(() => '')
      .tickSizeInner(-graphWidth)
      .tickSizeOuter(0);
    gridElem.call(yGrid).call(g =>
      g
        .selectAll('line')
        .attr('shape-rendering', 'crispEdges')
        .attr('stroke', '#9B96A4') // You can adjust the color here
        .attr('stroke-width', '1px'),
    );

    const flattenedData = this.groupData.flatMap(group =>
      group.members.map(member => ({
        ...member,
        groupName: group.name,
        groupId: group.id,
      })),
    );

    const u = select<SVGGElement, unknown>('#bars')
      .selectAll('rect')
      .data(flattenedData)
      .on('mouseover', (event: any, d: any) => mouseOver(event, d))
      //todo: put some rate limiting on the mousemove, I think it happens too fast
      .on('mousemove', (event: any, d: any) => mouseOver(event, d))
      .on('mouseleave', onLeave)
      .on('click', (event: any, d: any) => onClick(event, d));
    u.join('rect')
      .attr('x', (d: { groupName: string; name: string }) => {
        const group = this.groupData.find(g => g.name === d.groupName);
        const memberIndex =
          group?.members.findIndex(member => member.name === d.name) || 0;
        const adjustedBandwidth = x.bandwidth() / (group?.members.length || 1);
        return (x(d.groupName) || 0) + adjustedBandwidth * memberIndex;
      })
      .attr('y', (d: { max: NumberValue }) => y(d.max) || 0)
      .attr('width', (d: { groupName: string }) => {
        const group = this.groupData.find(g => g.name === d.groupName);
        return x.bandwidth() / (group?.members.length || 1) - 4;
      })
      .attr(
        'height',
        (d: { min: NumberValue; max: NumberValue }) =>
          (d.min !== null ? (y(d.min) as number) : 0) -
          (d.max ? (y(d.max) as number) : 0),
      )
      .attr('fill', (d: any) => d.color || '#51C2F2')
      .attr('opacity', (d: any) =>
        d.groupName + d.name != onHoverGroup && onHoverGroup !== ''
          ? '0.3'
          : '1.0',
      )
      .attr('cursor', (d: any) =>
        d.groupName + d.name == onHoverGroup ? 'pointer' : 'default',
      );

    const avgLineHeight = 4;

    const avgLines = select<SVGGElement, unknown>('#bars')
      .selectAll('.avg-line')
      .data(flattenedData)
      .on('mouseover', (event: any, d: any) => mouseOver(event, d))
      .on('mouseleave', onLeave)
      .on('click', (event: any, d: any) => onClick(event, d));

    avgLines
      .join('line')
      .attr('class', 'avg-line')
      .attr('x1', (d: { groupName: string; name: string }) => {
        const group = this.groupData.find(g => g.name === d.groupName);
        const memberIndex =
          group?.members.findIndex(member => member.name === d.name) || 0;
        const adjustedBandwidth = x.bandwidth() / (group?.members.length || 1);
        return (
          (x(d.groupName) || 0) +
          adjustedBandwidth * memberIndex +
          (d.groupName + d.name == onHoverGroup ? 1 : 0)
        );
      })
      .attr('x2', (d: { groupName: string; name: string }) => {
        const group = this.groupData.find(g => g.name === d.groupName);
        const memberIndex =
          group?.members.findIndex(member => member.name === d.name) || 0;
        const adjustedBandwidth = x.bandwidth() / (group?.members.length || 1);
        return (
          (x(d.groupName) || 0) +
          adjustedBandwidth -
          4 +
          (d.groupName + d.name == onHoverGroup ? -1 : 0) +
          adjustedBandwidth * memberIndex
        );
      })
      .attr('y1', (d: any) => {
        const avgLineY = y(d.avg) as number;
        return avgLineY;
      })
      .attr('y2', (d: any) => {
        const avgLineY = y(d.avg) as number;
        return avgLineY;
      })
      .attr('stroke', (d: any) => {
        if (this.isSingleRate(d.min, d.max, d.avg)) {
          return d.color || '#51C2F2'; // Use the color of the bar if all values are the same
        }
        return 'white';
      })
      .attr('opacity', (d: any) =>
        d.groupName + d.name != onHoverGroup &&
        onHoverGroup !== '' &&
        this.isSingleRate(d.min, d.max, d.avg)
          ? '0.3'
          : '1.0',
      )
      .attr('cursor', (d: any) =>
        d.groupName + d.name == onHoverGroup &&
        this.isSingleRate(d.min, d.max, d.avg)
          ? 'pointer'
          : 'default',
      )
      .attr('stroke-width', avgLineHeight);

    return {
      labelHoverName,
      svgSize,
      width: graphWidth + margin.left + margin.right,
      height,
      setYHoverable,
      onLeave,
      currentHoverable,
      calloutAnchor,
      toolTipVisible,
    };
  }

  AnchoredPosition = AnchoredPosition;
}
